// src/pages/Home.js
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const SLIDES = [
  {
    tag:     "Fast & Reliable",
    heading: ["Fast, Reliable Delivery", "Right to Your Doorstep"],
    accent:  0,
    sub:     "Dependable last-mile delivery across Lagos and Port Harcourt — every shipment tracked in real time and confirmed with proof of delivery.",
    cta:     "Request Pickup",
    bg:      "linear-gradient(135deg,rgba(11,30,61,0.88),rgba(11,30,61,0.75))",
    image:   "/Riders_image.png",
  },
  {
    tag:     "From Our Hands to Yours",
    heading: ["Safe hands.", "Swift delivery. Zero stress."],
    accent:  1,
    sub:     "Every item we pick up is handled with care and insured. From our hands to yours — that is our promise on every single delivery.",
    cta:     "Learn More",
    bg:      "linear-gradient(135deg,rgba(0,60,50,0.85),rgba(11,30,61,0.8))",
    image:   "/From_our_hands.jpg",
  },
  {
    tag:     "Team Pulse",
    heading: ["Accountable. Professional.", "Reliable."],
    accent:  0,
    sub:     "Our team is trained, uniformed, and committed to excellence. When you ship with Pulse, you're in safe hands — every time.",
    cta:     "About Us",
    bg:      "linear-gradient(135deg,rgba(11,30,61,0.82),rgba(26,52,96,0.78))",
    image:   "/Team_Pulse.jpg",
  },
  {
    tag:     "Pulse Logistics Platform",
    heading: ["Track your delivery", "live, anywhere."],
    accent:  1,
    sub:     "Our digital platform gives you real-time visibility on every shipment. Book online, track live, and get proof of delivery instantly.",
    cta:     "Track a Shipment",
    bg:      "linear-gradient(135deg,rgba(11,30,61,0.88),rgba(0,100,88,0.8))",
    image:   "/Pulse_mockup_1.png",
  },
];

export default function Home() {
  const [current, setCurrent] = useState(0);
  const [awb, setAwb]         = useState("");
  const [paused, setPaused]   = useState(false);
  const navigate              = useNavigate();

  const next = useCallback(() => setCurrent(c => (c + 1) % SLIDES.length), []);
  const prev = () => setCurrent(c => (c - 1 + SLIDES.length) % SLIDES.length);

  // Auto-advance every 5 seconds
  useEffect(() => {
    if (paused) return;
    const t = setInterval(next, 5000);
    return () => clearInterval(t);
  }, [paused, next]);

  const handleTrack = () => {
    if (awb.trim()) navigate(`/track?awb=${awb.trim()}`);
  };

  const slide = SLIDES[current];

  const services = [
    { icon: "🚚", title: "Last-Mile Delivery",        desc: "Door-to-door delivery with real-time confirmation across Lagos and Port Harcourt." },
    { icon: "🛡️", title: "Insured Deliveries",        desc: "Every item entrusted to us is insured. Your goods are safe with us — always." },
    { icon: "📦", title: "Interstate Freight",         desc: "Lagos ↔ Port Harcourt corridor handled with full chain-of-custody visibility." },
    { icon: "🏭", title: "Bulk & Vendor Delivery",     desc: "Preferential rates for online vendors and repeat customers. Scale with us." },
    { icon: "🔄", title: "Fragile & Special Handling", desc: "Manufacturers, designers, pharmacies — we handle high-value items with care." },
    { icon: "⚡", title: "Same-Day Delivery",          desc: "Urgent shipments handled same day within Lagos and Port Harcourt." },
  ];

  const whyUs = [
    { icon: "🛡️", title: "Every delivery is insured",  desc: "Your goods are protected from pickup to drop-off. No other logistics company in our corridor offers this." },
    { icon: "📱", title: "Real-time tracking",          desc: "Track your shipment live. No more guessing — you see exactly where your goods are." },
    { icon: "📞", title: "We pick up. Always.",         desc: "No unanswered calls after pickup. Our team is reachable Mon–Sat, 8am–8pm." },
    { icon: "💰", title: "Transparent pricing",         desc: "No hidden fees. No surprise charges. Get your instant quote before you book." },
  ];

  const testimonials = [
    { name: "Adaeze O.",   role: "Fashion Vendor, Lagos",      text: "I've used 4 logistics companies this year. Pulse is the only one that gives me proof of delivery every single time." },
    { name: "Emeka N.",    role: "Online Seller, Port Harcourt", text: "The tracking feature changed everything for my business. No more 'where is my order' messages from customers." },
    { name: "Chidinma P.", role: "Bulk Buyer, Lagos–PHC",      text: "I send goods from Lagos to PH every week. Pulse has never lost a single item. And they actually pick up their phone!" },
  ];

  return (
    <div>

      {/* ══════════════════════════════════════════
          HERO SLIDER
      ══════════════════════════════════════════ */}
      <section
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        style={{ position: "relative", overflow: "hidden", minHeight: 520 }}>

        {/* Background image */}
        {slide.image && (
          <div style={{
            position: "absolute", inset: 0,
            backgroundImage: `url(${slide.image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            transition: "opacity .6s ease",
          }} />
        )}

        {/* Dark overlay with brand color tint */}
        <div style={{
          position: "absolute", inset: 0,
          background: slide.bg,
          transition: "background .6s ease",
        }} />

        {/* Subtle grid overlay */}
        <div style={{ position: "absolute", inset: 0, opacity: .03, backgroundImage: "repeating-linear-gradient(0deg,#fff 0,#fff 1px,transparent 1px,transparent 60px),repeating-linear-gradient(90deg,#fff 0,#fff 1px,transparent 1px,transparent 60px)", pointerEvents: "none" }} />

        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "clamp(40px,8vw,80px) 20px clamp(60px,8vw,72px)", display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: "clamp(16px,4vw,40px)", alignItems: "center", position: "relative", zIndex: 2 }}>

          {/* Left — Slide content */}
          <div style={{ maxWidth: 600 }}>
            {/* Tag */}
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 14px", borderRadius: 20, background: "rgba(0,198,215,.15)", border: "1px solid rgba(0,198,215,.25)", marginBottom: 24 }}>
              <span className="live-dot" />
              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#00C6D7", letterSpacing: ".1em" }}>
                {slide.tag}
              </span>
            </div>

            {/* Heading */}
            <h1 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700, fontSize: "clamp(20px,3.5vw,38px)", lineHeight: 1.25, color: "#fff", marginBottom: 16 }}>
              {slide.accent === 0 ? (
                <>
                  <span style={{ backgroundImage: "linear-gradient(135deg,#00C6D7,#00A896)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                    {slide.heading[0]}
                  </span>
                  <br />{slide.heading[1]}
                </>
              ) : (
                <>
                  {slide.heading[0]}
                  <br />
                  <span style={{ backgroundImage: "linear-gradient(135deg,#00C6D7,#00A896)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                    {slide.heading[1]}
                  </span>
                </>
              )}
            </h1>

            <p style={{ fontSize: 15, color: "rgba(255,255,255,.6)", lineHeight: 1.8, marginBottom: 36, maxWidth: 480 }}>
              {slide.sub}
            </p>

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 32 }}>
              <button className="btn btn-primary"
                onClick={() => {
                if (slide.cta === "Learn More" || slide.cta === "About Us") navigate("/about");
                else if (slide.cta === "Track a Shipment") navigate("/track");
                else navigate("/request");
              }}
                style={{ padding: "13px 28px", fontSize: 14, fontWeight: 600 }}>
                {slide.icon} {slide.cta}
              </button>
              <a href="https://wa.me/2348023753147?text=Hello%20Pulse%20Logistics!"
                target="_blank" rel="noopener noreferrer"
                className="btn"
                style={{ background: "#25D366", color: "#fff", padding: "13px 24px", fontSize: 14, fontWeight: 600 }}>
                💬 WhatsApp Us
              </a>
            </div>

            {/* Track bar */}
            <div style={{ display: "flex", maxWidth: 420, background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.15)", borderRadius: 8, overflow: "hidden" }}>
              <input value={awb} onChange={e => setAwb(e.target.value)}
                placeholder="Track a shipment — enter AWB…"
                onKeyDown={e => e.key === "Enter" && handleTrack()}
                style={{ flex: 1, border: "none", background: "transparent", color: "#fff", padding: "12px 16px", fontSize: 13, outline: "none" }} />
              <button className="btn btn-primary" onClick={handleTrack}
                style={{ borderRadius: 0, padding: "0 18px", fontSize: 13 }}>
                Track
              </button>
            </div>
          </div>

          {/* Right — Floating stats cards */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10, paddingRight: 4 }}>
            {[
              { value: "100%",   label: "🛡️ Insured" },
              { value: "1,200+", label: "📦 Delivered" },
              { value: "98.3%",  label: "⚡ On-time" },
            ].map(s => (
              <div key={s.value} style={{ background: "rgba(255,255,255,.12)", backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,.2)", borderRadius: 10, padding: "10px 14px", textAlign: "center", minWidth: "clamp(90px,15vw,130px)" }}>
                <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: "clamp(16px,2.5vw,22px)", color: "#00C6D7" }}>{s.value}</div>
                <div style={{ fontSize: "clamp(9px,1.5vw,11px)", color: "rgba(255,255,255,.7)", marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Slider Controls ── */}
        {/* Arrows */}
        <button onClick={prev}
          style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", width: 40, height: 40, borderRadius: "50%", background: "rgba(255,255,255,.12)", border: "1px solid rgba(255,255,255,.2)", color: "#fff", fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "background .2s" }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(0,198,215,.3)"}
          onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,.12)"}>
          ‹
        </button>
        <button onClick={next}
          style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", width: 40, height: 40, borderRadius: "50%", background: "rgba(255,255,255,.12)", border: "1px solid rgba(255,255,255,.2)", color: "#fff", fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "background .2s" }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(0,198,215,.3)"}
          onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,.12)"}>
          ›
        </button>

        {/* Dots */}
        <div style={{ position: "absolute", bottom: 20, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 8 }}>
          {SLIDES.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)}
              style={{ width: i === current ? 24 : 8, height: 8, borderRadius: 4, background: i === current ? "#00C6D7" : "rgba(255,255,255,.35)", border: "none", cursor: "pointer", transition: "all .3s", padding: 0 }} />
          ))}
        </div>

        {/* Slide counter */}
        <div style={{ position: "absolute", bottom: 20, right: 24, fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "rgba(255,255,255,.4)" }}>
          {String(current + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
        </div>
      </section>

      {/* ── Stats bar ── */}
      <section style={{ background: "#0B1E3D" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 24px", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 20 }}>
          {[
            { value: "1,200+", label: "Shipments Delivered" },
            { value: "98.3%",  label: "On-Time Rate" },
            { value: "2",      label: "City Hubs" },
            { value: "100%",   label: "Insured Deliveries" },
          ].map(s => (
            <div key={s.label} style={{ textAlign: "center" }}>
              <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 24, backgroundImage: "linear-gradient(135deg,#00C6D7,#00A896)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{s.value}</div>
              <div style={{ color: "rgba(255,255,255,.4)", fontSize: 12, marginTop: 3 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Services ── */}
      <section style={{ background: "#fff", padding: "64px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ marginBottom: 40 }}>
            <span className="tag tag-cyan" style={{ marginBottom: 10, display: "inline-block" }}>Our Services</span>
            <h2 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 28, color: "#0B1E3D" }}>Everything your business needs</h2>
            <p style={{ color: "#6B7280", fontSize: 14, marginTop: 8, maxWidth: 460 }}>From single parcels to bulk freight — Lagos, Port Harcourt, and interstate.</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 16 }}>
            {services.map(sv => (
              <div key={sv.title} className="card card-hover">
                <div style={{ width: 44, height: 44, borderRadius: 10, marginBottom: 16, background: "linear-gradient(135deg,rgba(0,198,215,.1),rgba(0,168,150,.1))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>{sv.icon}</div>
                <h3 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 15, color: "#0B1E3D", marginBottom: 8 }}>{sv.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 13, lineHeight: 1.65 }}>{sv.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why Pulse ── */}
      <section style={{ background: "#F7FAFB", padding: "64px 24px", borderTop: "1px solid #E2EBF0" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <span className="tag tag-navy" style={{ marginBottom: 12, display: "inline-block" }}>Why Pulse</span>
            <h2 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 28, color: "#0B1E3D" }}>We're different. Here's proof.</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 24 }}>
            {whyUs.map(w => (
              <div key={w.title} style={{ textAlign: "center", padding: "24px 16px" }}>
                <div style={{ fontSize: 36, marginBottom: 16 }}>{w.icon}</div>
                <h4 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 15, color: "#0B1E3D", marginBottom: 8 }}>{w.title}</h4>
                <p style={{ color: "#6B7280", fontSize: 13, lineHeight: 1.65 }}>{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section style={{ background: "#fff", padding: "64px 24px", borderTop: "1px solid #E2EBF0" }}>
        <div style={{ maxWidth: 800, margin: "0 auto", textAlign: "center" }}>
          <span className="tag tag-cyan" style={{ marginBottom: 12, display: "inline-block" }}>How It Works</span>
          <h2 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 28, color: "#0B1E3D", marginBottom: 40 }}>Three steps to your delivery</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 28 }}>
            {[
              { step: "01", title: "Book Your Pickup",  desc: "Fill our form or send us a WhatsApp message. Get an instant price quote." },
              { step: "02", title: "We Collect & Move", desc: "Our rider picks up from your location. Every item is logged and insured." },
              { step: "03", title: "Track & Confirm",   desc: "Follow your shipment live. Get POD confirmation the moment it's delivered." },
            ].map(s => (
              <div key={s.step}>
                <div style={{ width: 48, height: 48, borderRadius: "50%", margin: "0 auto 14px", background: "linear-gradient(135deg,#00C6D7,#00A896)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, fontSize: 13, color: "#fff" }}>{s.step}</div>
                <h4 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 14, color: "#0B1E3D", marginBottom: 8 }}>{s.title}</h4>
                <p style={{ color: "#6B7280", fontSize: 13, lineHeight: 1.65 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section style={{ background: "#F7FAFB", padding: "64px 24px", borderTop: "1px solid #E2EBF0" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <span className="tag tag-green" style={{ marginBottom: 12, display: "inline-block" }}>Testimonials</span>
            <h2 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 28, color: "#0B1E3D" }}>What our clients say</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 20 }}>
            {testimonials.map(t => (
              <div key={t.name} className="card" style={{ borderLeft: "3px solid #00C6D7" }}>
                <p style={{ color: "#374151", fontSize: 14, lineHeight: 1.8, marginBottom: 20, fontStyle: "italic" }}>"{t.text}"</p>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: "50%", background: "linear-gradient(135deg,#00C6D7,#00A896)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Syne',sans-serif", fontWeight: 800, color: "#fff", fontSize: 15 }}>{t.name[0]}</div>
                  <div>
                    <div style={{ fontWeight: 700, color: "#0B1E3D", fontSize: 13 }}>{t.name}</div>
                    <div style={{ color: "#9CA3AF", fontSize: 11 }}>{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ padding: "64px 24px", background: "#fff" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ background: "linear-gradient(135deg,#0B1E3D,#1A3460)", borderRadius: 14, padding: "48px 44px", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 24 }}>
            <div>
              <h3 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 26, color: "#fff", marginBottom: 8 }}>Ready to move your goods?</h3>
              <p style={{ color: "rgba(255,255,255,.45)", fontSize: 13 }}>Mon–Sat · 8:00am – 8:00pm · Lagos & Port Harcourt</p>
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <a href="https://wa.me/2348023753147" target="_blank" rel="noopener noreferrer" className="btn btn-wa" style={{ fontSize: 14, padding: "12px 22px" }}>💬 WhatsApp Us</a>
              <button className="btn btn-primary" onClick={() => navigate("/request")} style={{ fontSize: 14, padding: "12px 22px" }}>📦 Book Online</button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={{ background: "#0B1E3D" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "52px 24px 36px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1.5fr repeat(3,1fr)", gap: 40, marginBottom: 44 }}>
            <div>
              <img src="/logo.jpeg" alt="Pulse Logistics" style={{ height: 28, filter: "brightness(0) invert(1)", marginBottom: 16, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
              <p style={{ color: "rgba(255,255,255,.35)", fontSize: 12, lineHeight: 1.8, marginBottom: 16 }}>
                Safe hands. Swift delivery. Zero stress.<br />Every delivery is insured.
              </p>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.3)", lineHeight: 2, fontFamily: "'JetBrains Mono',monospace" }}>
                <div>📍 Lagos Hub — Jibowu</div>
                <div>📍 PH Hub — Waterlines</div>
                <div>🕐 Mon–Sat: 8am – 8pm</div>
              </div>
            </div>
            {[
              { heading: "Services", links: ["Last-Mile Delivery", "Interstate Freight", "Bulk & Vendor Delivery", "Insured Handling"] },
              { heading: "Company",  links: ["About Us", "Our Team", "Careers", "Blog"] },
              { heading: "Support",  links: ["Track Order", "Book a Pickup", "Contact Us", "FAQs"] },
            ].map(col => (
              <div key={col.heading}>
                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: ".14em", color: "#00C6D7", marginBottom: 14, textTransform: "uppercase", fontWeight: 600 }}>{col.heading}</div>
                {col.links.map(l => (
                  <div key={l} style={{ color: "rgba(255,255,255,.4)", fontSize: 12, marginBottom: 10, cursor: "pointer", transition: "color .15s" }}
                    onMouseEnter={e => e.target.style.color = "#fff"}
                    onMouseLeave={e => e.target.style.color = "rgba(255,255,255,.4)"}>{l}</div>
                ))}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10, marginBottom: 24 }}>
            {[
              { handle: "@pulselogisticsltd", url: "https://instagram.com/pulselogisticsltd" },
              { handle: "@Pulse Logistics",   url: "https://facebook.com" },
              { handle: "@pulselogistics5",   url: "https://tiktok.com/@pulselogistics5" },
            ].map(s => (
              <a key={s.handle} href={s.url} target="_blank" rel="noopener noreferrer"
                style={{ padding: "6px 12px", borderRadius: 6, background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.08)", color: "rgba(255,255,255,.5)", fontSize: 11, textDecoration: "none", fontFamily: "'JetBrains Mono',monospace", transition: "all .15s" }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(0,198,215,.12)"; e.currentTarget.style.color = "#00C6D7"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.05)"; e.currentTarget.style.color = "rgba(255,255,255,.5)"; }}>
                {s.handle}
              </a>
            ))}
          </div>
          <div style={{ borderTop: "1px solid rgba(255,255,255,.06)", paddingTop: 20, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <span style={{ color: "rgba(255,255,255,.2)", fontSize: 11, fontFamily: "'JetBrains Mono',monospace" }}>© 2026 Pulse Logistics Company Ltd</span>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ color: "rgba(255,255,255,.18)", fontSize: 10, fontFamily: "'JetBrains Mono',monospace" }}>Designed & Built by</span>
              <span style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 12, backgroundImage: "linear-gradient(135deg,#00C6D7,#00A896)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Comvara</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}